export interface BlogPost {
  slug: string
  title: string
  /** Shorter title for the browser tab and search results when `title` runs past about 60 characters with the brand. */
  seoTitle?: string
  excerpt: string
  category: 'VAT Guides' | 'Tax Guides' | 'Accounting Tips' | 'Invoicing' | 'Product Updates' | 'How-to guides'
  content: string
  author: string
  /** Day the post first went on the site: its text was first committed on 7 February 2026 (6056218). Noon UTC, so the
   * day reads the same in every time zone. */
  publishedDate: string
  /** Date its text last changed. Not a review date: no post has been reviewed by an accountant. */
  updatedDate?: string
  /**
   * Its tax instructions were withdrawn pending an accountant's review (launch item L03). The page stays reachable
   * for old links but is noindex, left out of the guide list and the sitemap. The withdrawn text is in git at
   * 29f54a5 and listed in Docs/research/2026-09-24-accountant-review-pack.md. On 27.09.2026 the owner chose to
   * publish rewritten guides instead: every rule is taken from a linked Revenue or gov.ie page with its date, and
   * none of the withdrawn text came back. No post is held today; still none has been reviewed by an accountant.
   */
  heldForReview?: boolean
  /** Alt text for the guide's photo, public/images/guides-20260928/<slug>-{640,960,1536}.webp (Gemini, 28.09.2026). */
  imageAlt: string
  readTime: number
  keywords: string[]
  featured?: boolean
}

const posts: BlogPost[] = [
  {
    slug: 'first-invoice-with-a-discount',
    title: 'Your first invoice in NumWise, with a discount',
    excerpt: 'Add the customer, write the lines, take a discount off before VAT, check the totals and issue the invoice, with the names of the buttons you will see.',
    category: 'How-to guides',
    imageAlt: "A finished oak side table on a carpenter's workbench, with a phone and a printed job sheet beside it",
    author: 'NumWise editorial team',
    publishedDate: '2026-09-25T12:00:00Z',
    updatedDate: '2026-09-28T12:00:00Z',
    readTime: 4,
    keywords: ['first invoice NumWise', 'invoice discount before VAT', 'how to issue an invoice', 'NumWise how-to'],
    featured: true,
    content: `# Your first invoice in NumWise, with a discount

This guide follows one invoice from an empty form to an issued document, with a customer, two lines, a 10% discount and the totals that result.

## 1. Add the customer

1. Open **Customers** and choose **Add customer**.
2. Enter the customer's **Name**. The other fields are optional.
3. Add an **Email** if you want to email the invoice from NumWise, and a **VAT number** if the customer is a business that has one.
4. Choose **Create customer**.

You can also add a customer while you write the invoice: open the customer list on the invoice and choose **Create new customer**.

## 2. Start the draft

1. Open **Invoices** and choose **New invoice**.
2. Under **Who and when**, choose the **Customer**. The currency changes to the customer's default currency.
3. Check the **Issue date** and the **Due date**. The **Invoice number** is set by NumWise, in order.

## 3. Write the lines

Under **What you are charging for**, enter a **Description**, **Qty** and **Unit price** for each line. Use **Add line item** for another line. If you are registered for VAT, choose the **VAT rate** for each line; if you are not, the lines carry no VAT.

## 4. Take off the discount

In the **Discount** section, open **Discount** and choose one of:

- **Percentage of the net**, then enter the **Discount (%)**; or
- **Fixed amount**, then enter the **Discount amount** in the invoice currency.

The discount is taken off the whole invoice before VAT and shared across the lines. When the lines have different VAT rates, each rate carries its share, so the VAT is worked out on the discounted amounts.

## 5. Check the totals

The **Summary** beside the form shows:

- **Subtotal before discount**: the lines added up, without VAT;
- **Discount**: the amount taken off;
- the net after the discount;
- the VAT for each rate;
- **Total, VAT included**: what your customer pays.

| Example (illustrative) | Amount |
|---|---|
| Two lines at 23% VAT | 1,120.00 |
| Discount 10% | -112.00 |
| Net after discount | 1,008.00 |
| VAT at 23% | 231.84 |
| Total, VAT included | 1,239.84 |

A percentage discount cannot be more than 100%, and a fixed discount cannot be larger than the net of the lines.

## 6. Save, then issue

1. Choose **Create invoice**. NumWise saves it as a draft and opens it.
2. Check it with **Download PDF**. A draft can still be changed with **Edit draft**.
3. When it is right, choose **Send email** to email it with the PDF and a link to view it, or **Issue invoice** if you deliver it another way.

Sending an email needs an email account connected in **Settings**, **Email sending**, by the owner or an admin.

## After it is issued

An issued invoice keeps its number and its content. The discount can only be changed while the invoice is a draft. To correct an issued invoice, issue a credit note against it: credit notes are included on every plan.

When the money arrives, choose **Record payment** on the invoice, or match the payment from your bank statement. A part payment leaves the invoice **Part paid**, with the balance due shown.`,
  },
  {
    slug: 'check-a-receipt-before-you-save',
    title: 'Check a receipt read by NumWise before you save the cost',
    excerpt: 'Add a receipt photo or a supplier invoice, let NumWise fill the expense form, then check each value against the document before you save, field by field.',
    category: 'How-to guides',
    imageAlt: 'A tradesperson in a parked van photographs a crumpled till receipt with a phone',
    author: 'NumWise editorial team',
    publishedDate: '2026-09-25T12:00:00Z',
    updatedDate: '2026-09-28T12:00:00Z',
    readTime: 4,
    keywords: ['receipt scanning NumWise', 'check receipt OCR', 'add an expense from a receipt', 'NumWise how-to'],
    content: `# Check a receipt read by NumWise before you save the cost

NumWise can read a receipt or a supplier invoice and fill in the expense form for you. Reading is not perfect, so nothing is saved until you check the form and choose **Create Expense**.

## 1. Add the document

1. Open **Expenses** and choose **New expense**.
2. Under **Start from the document**, choose **Add a receipt photo** for a till receipt, or **Add a supplier invoice** for an invoice from a supplier.
3. Take a photo with your phone or choose a file: a PDF, JPEG, PNG or WebP file of up to 10MB.

While NumWise reads it you see **Reading the receipt...** (or **Reading the invoice...**). Then **Receipt read** (or **Invoice read**) appears with a confidence level (High, Medium or Low) and what was found, such as the supplier, date and total.

## 2. Check each value against the document

The form now holds what was read, and the **Summary** says **Read from the receipt**. Hold the receipt next to the screen and check:

- **Description** and **Shop or supplier name**: the right shop, and a description you will recognise later.
- **Amount incl. VAT**: the total you paid, with the right currency.
- **Date**: the date on the receipt, not the day you photograph it.
- **Category**: one of the standard categories. What you can claim is for you and your accountant to decide.

Look twice at a Low or Medium confidence, and check a High one as well.

## 3. Check the VAT

Under **VAT and business use**, tick **VAT is reclaimable** only if you are entitled to reclaim the VAT on this purchase. When NumWise finds a VAT rate on the document, it ticks the box for you: untick it if the VAT cannot be reclaimed, for example when you are not registered for VAT.

With the box ticked, choose the **VAT rate**. The **VAT amount** is worked out from the amount and the rate, so check that it matches the VAT printed on the receipt.

| Example (illustrative) | Amount |
|---|---|
| Amount incl. VAT | 104.00 |
| VAT at 23%, worked out | 19.45 |
| Net | 84.55 |

## 4. Set the business share

For something you also use privately, such as a phone or a car, set **Business use (%)**. The **Summary** then shows the **Business share of the total** and the **VAT to reclaim**. Reports count only the business share, and only that share of the VAT.

## 5. Save with the document attached

The file you added is attached to the expense under **Document**. When every value matches, choose **Create Expense**. The receipt stays with the cost, and you can download it later from the expense.

## Good to know

- Your plan sets how many documents are read in 30 days, failed reads included: 20 on Lite, 100 on Starter, 200 on Business and 400 on Professional.
- You can always type the details yourself, with or without a document attached.`,
  },
  {
    slug: 'prepare-an-export-for-your-accountant',
    title: 'Prepare an export of your records for your accountant',
    seoTitle: 'Prepare an export for your accountant',
    excerpt: 'Prepare one Accountant pack with period reports, registers and available source documents. Check the gaps before handing it over, or export single reports.',
    category: 'How-to guides',
    imageAlt: 'A sole trader hands a green folder of records across a desk to an accountant',
    author: 'NumWise editorial team',
    publishedDate: '2026-09-25T12:00:00Z',
    updatedDate: '2026-09-28T12:00:00Z',
    readTime: 4,
    keywords: ['export for accountant', 'NumWise Excel export', 'VAT3 draft export', 'NumWise how-to'],
    content: `# Prepare an export of your records for your accountant

Your accountant needs the figures for a period and the records behind them. **Accountant pack** brings those together in one ZIP, on every plan. You can also export individual reports or download an all-time workbook of records.

## 1. Bring the period up to date

Before you export, make sure the period is complete in NumWise:

- the invoices for the period are issued, and the payments you received are recorded or matched from your bank statement;
- the costs are in, with their receipts attached;
- the bank statement for the period is imported and its lines are matched.

## 2. Prepare the Accountant pack

Open **Reports**, **Accountant pack**. Choose the dates your accountant asked for, then select **Prepare pack**. Agree the reporting dates with them: a calendar month or quarter is not automatically your VAT filing period.

Review the checks for missing cost documents, categories, pending inbox documents and bank lines still to reconcile. VAT-registered businesses also see warnings from the VAT3 draft. Your accountant still reviews the figures, and these checks only help you prepare.

Select **Download ZIP**. It contains:

- profit and loss, plus VAT summary and a VAT3 draft for VAT-registered businesses;
- an Excel register of invoices, costs, credit notes, line items, payments and supporting records;
- available attached source documents, a document index, a README and a manifest recording the export.

Amounts in the pack's register are numeric currency amounts, with separate labelled columns for original currency and euro values. Issued documents retain their recorded party details, separately from current contacts.

The pack includes documents dated in the period and earlier documents paid during it. Statuses and balances are current when read, not a reconstruction of the period end. It is a working copy: preparation does not lock the period, send an email or file a return. If you edit records, choose **Refresh records** before downloading again.

## 3. Check the document index

The ZIP includes available source attachments. Native NumWise invoices and credit notes are included as records and line items, not newly generated PDF copies. The index identifies missing, inaccessible, unsupported or oversized files.

Each source file can be up to 10 MB, with up to 100 MB of attachments per pack. For larger collections, export a shorter period or download files separately. **Rebuild and download ZIP** retries file retrieval using the prepared records; use **Refresh records** first if the records themselves changed.

## 4. Add individual reports if needed

You can still open each report and choose the dates and **Export Excel** or **Export CSV**. Every report explains its basis under **How this is counted**:

| Report | What it shows | Export |
|---|---|---|
| Profit and loss | Income less expenses, counted on the payment dates | Excel or CSV |
| VAT summary | VAT on sales and on purchases, by rate | Excel or CSV |
| VAT3 draft for review | The VAT3 boxes, for your accountant to check | Excel or CSV |
| Cash flow | Money in and out, month by month | Excel or CSV |
| Expense detail | Your costs, by category | Excel or CSV |
| Income | Invoices issued, by customer and by month | CSV |

The VAT figures are drafts. Your accountant checks them and files the return on ROS.

## 5. Keep an all-time workbook if needed

For a raw workbook separate from the period pack, the owner or an admin can download the workspace records:

1. Open **Settings**, **Organisation**.
2. Under **Your data**, choose **Export all data (Excel)**.
3. Wait while it shows **Preparing export...**. The file name starts with **numwise-data-** and today's date.

The workbook has one sheet for each kind of record: customers, suppliers, invoices and their lines, payments, credit notes, expenses, bank transactions and recurring invoices. The data is as stored, so amounts are in cents (12345 means 123.45) and dates are written year-month-day.

This all-time workbook does not contain receipt files. Use Accountant pack for the period's available attachments, or open an expense and download its document separately.

## Or give them their own login

On the Business and Professional plans, you can invite your accountant from **Settings**, **Team** with the Accountant role. They sign in with their own login, see the records and reports, and export what they need themselves.`,
  },
  {
    slug: 'best-accounting-software-ireland',
    title: 'Best Small Business Accounting Software in Ireland: Sage, Xero, QuickBooks and NumWise Compared',
    seoTitle: 'Best Small Business Accounting Software in Ireland',
    excerpt: 'Sage, Xero, QuickBooks and NumWise compared for Irish small businesses and sole traders: prices, logins, bank transactions and VAT, checked 28 September 2026.',
    category: 'Accounting Tips',
    imageAlt: 'Four closed notebooks in different colours side by side on a desk, a hand resting on one',
    author: 'NumWise editorial team',
    publishedDate: '2026-02-07T12:00:00Z',
    updatedDate: '2026-09-29T12:00:00Z',
    readTime: 8,
    keywords: [
      'best accounting software for small business Ireland',
      'small business accounting software Ireland',
      'Xero vs QuickBooks',
      'Sage vs QuickBooks',
      'accounting software Ireland',
    ],
    // Rewritten on 28.09.2026 from a checklist into a comparison (owner decision, keyword research the same day).
    // Every competitor figure comes from that product's Irish pricing page, fetched 28.09.2026 and linked in the text.
    // Where a pricing page does not state something, the table says so instead of filling the gap.
    content: `# Best Small Business Accounting Software in Ireland: Sage, Xero, QuickBooks and NumWise Compared

We make NumWise, so weigh this comparison accordingly. Every price and plan detail for the other three products comes from their own Irish pricing pages, checked on 28 September 2026 and linked below. Where a pricing page is silent, the table says so. Prices change: Xero, for example, says its prices go up on 1 November 2026. Check each page again before you buy.

## At a glance

| | Sage Accounting | Xero | QuickBooks Online | NumWise |
|---|---|---|---|---|
| Entry plan, list price a month | €19 + VAT (Accounting Start) | US$29 (Starter) | €21 (Simple Start) | €9 (Lite) |
| Other plans a month | €37 + VAT (Accounting) | US$50 (Standard), US$75 (Premium) | €31, €45, €75 | €19, €39, €79 |
| Currency you pay in | Euro | US dollars | Euro | Euro |
| People who can log in | 1 on Accounting Start, unlimited on Accounting | Not stated on the pricing page | 1, 3, 5 or 25 by plan, plus your accountant | 1, 1, 3 or 10 by plan |
| Limits on the entry plan | Not stated on the pricing page | Quotes, 20 invoices and 5 bills | 1 user | 5 invoices and quotes a month |
| Free trial | 1 month, instead of the introductory discount | 30 days | 30 days, not combined with the introductory discount | 7 days on Starter, no card |
| Bank transactions | Automatic bank reconciliation | Reconcile bank transactions (see Xero's note on Irish bank feeds) | Automated bank feeds | Statement files you download from your bank |
| VAT | "Calculate and create VAT returns" | Not stated on the pricing page | Not stated on the pricing page | VAT3 drafts for you to check; nothing is filed |
| Payroll | Add-on from €14 + VAT a month | Not on the pricing page | Not on the pricing page | Not included |

List prices are the regular monthly prices after any introductory offer. On 28 September 2026 Sage offered 90% off for 3 months, Xero 80% off for 3 months and QuickBooks 90% off for 6 months.

## Sage Accounting

Two plans. Accounting Start is for one user; Accounting allows unlimited users and adds quotes, purchase invoices, receipt capture, cash flow forecasts, multi-currency and inventory. Sage quotes both prices plus VAT, and its pricing page lists calculating and creating VAT returns. Payroll is a separate add-on, from €14 + VAT a month for 5 employees.

Suits you if you want VAT returns, several people in the books, or payroll from the same supplier.

## Xero

Three plans on the Irish pricing page: Starter, Standard and Premium. Xero lists its Irish prices in US dollars, so what you pay in euro moves with the exchange rate. Starter is limited to quotes, 20 invoices and 5 bills; Standard and Premium remove those limits, and only Premium handles multiple currencies. Expense claims and project tracking are paid add-ons. Xero's pricing page also carries a note that bank feeds for Irish banks depend on its regulatory status and may need transactions imported manually, so ask about your own bank before you commit.

Suits you if you want a large app ecosystem and are comfortable paying in dollars.

## QuickBooks Online

Four plans in euro: Simple Start, Essentials, Plus and Advanced, for 1, 3, 5 or 25 users, each with access for your accountant. Every plan lists automated bank feeds. You choose between a 30-day free trial and the introductory discount; the discount terms say you cannot have both.

Suits you if you want bank feeds and expect your team to grow.

## NumWise

Four plans in euro: Lite, Starter, Business and Professional. The price shown is the price charged; nothing is added at checkout. Each invoice line takes its own Irish VAT rate, and the VAT report and VAT3 draft are built from your invoices and costs for you and your accountant to check. NumWise does not file anything with Revenue.

Bank transactions come in from the statement files your bank lets you download (CSV, Excel, OFX, QIF, MT940, CAMT.053 or PDF). NumWise never asks for your online banking login, and it has no live bank feeds. Payroll, stock, RCT ([Relevant Contracts Tax](https://www.revenue.ie/en/self-assessment-and-self-employment/rct/index.aspx)), a balance sheet and tax filing are outside its scope. The owner can download every record as one Excel workbook at any time.

Suits you if you are a sole trader or small business that wants a low monthly cost, Irish VAT drafts and records your accountant can check, and you do not need payroll or live bank feeds.

## Quick answers

**Which is cheapest?** By list price for the entry plan: NumWise €9, Sage €19 + VAT, QuickBooks €21 and Xero US$29 a month. Entry plans differ in limits, so compare the plan you would need: NumWise Lite, for example, covers 5 invoices and quotes a month.

**Xero or QuickBooks?** Both have free 30-day trials. QuickBooks prices its Irish plans in euro and states a user count for each plan; Xero prices in US dollars and limits Starter to 20 invoices and 5 bills. Multi-currency is on Xero Premium only.

**Sage or QuickBooks?** Sage has two plans, quotes them plus VAT and lists VAT returns and a payroll add-on. QuickBooks has four plans with a fixed user count each and lists automated bank feeds on all of them.

**Which works for a sole trader?** Any of the four entry plans is sized for one person. The deciding questions are usually volume (how many invoices a month), whether you need payroll, and how you want bank transactions to arrive.

## Seven questions to ask any product

1. **How do bank transactions get in?** A bank feed, or statement files you download?
2. **What does it do with VAT?** Record it, prepare figures for review, or file a return?
3. **How do receipts get in, and where do they stay?**
4. **Who can log in,** and does your accountant need a seat?
5. **What does it cost after any introductory offer,** and what happens when you stop paying?
6. **Can you take everything with you** if you leave?
7. **What is outside its scope,** such as payroll, stock or RCT?

Revenue explains the VAT return itself in [How do you complete a VAT 3 return?](https://www.revenue.ie/en/vat/accounting-for-vat/how-to-account-for-value-added-tax/completing-vat3-return.aspx)

## Before you decide

Try each product with one real month of your own records: a few invoices, a handful of receipts and one bank statement. That shows more than any comparison page, including this one.

## Sources

Checked on 28 September 2026:

- Sage Accounting pricing, Ireland: [sage.com/en-ie](https://www.sage.com/en-ie/sage-business-cloud/accounting/pricing/)
- Xero pricing plans, Ireland: [xero.com/ie](https://www.xero.com/ie/pricing-plans/)
- QuickBooks Online pricing, Ireland: [quickbooks.intuit.com/ie](https://quickbooks.intuit.com/ie/pricing/)
- NumWise pricing: [numwise.eu/pricing](https://numwise.eu/pricing)

---

*Want to try that month in NumWise? [Start a 7-day free trial](/register). No card needed.*
`
  },
  {
    slug: 'how-to-file-vat3-return-ireland',
    title: 'How to Complete a VAT3 Return in Ireland',
    excerpt: 'What goes in each VAT3 box (T1 to T4, E1, E2, ES1, ES2, PA1) as Revenue defines it, when the return is due, and what the NumWise VAT3 draft prepares.',
    category: 'VAT Guides',
    imageAlt: 'Invoices sorted into two piles on a desk, with a calculator and a marked wall calendar behind',
    author: 'NumWise editorial team',
    publishedDate: '2026-02-07T12:00:00Z',
    updatedDate: '2026-09-29T12:00:00Z',
    readTime: 6,
    keywords: ['VAT3 return', 'how to complete a VAT3 return', 'VAT3 boxes', 'Irish VAT return'],
    content: `# How to Complete a VAT3 Return in Ireland

The VAT3 return records the VAT you must pay, or can reclaim, for one taxable period. Four boxes carry the VAT: T1 (VAT on sales), T2 (VAT on purchases), and T3 or T4 (the difference, payable or repayable). Five more boxes carry values for trade with other EU countries and for imports under postponed accounting. The return is due by the 19th of the month after the period, or the 23rd if you file on ROS.

The rules below come from Revenue's own pages, linked in each section and listed at the end, all checked on 27 September 2026.

## The boxes, as Revenue defines them

From [How do you complete a VAT 3 return?](https://www.revenue.ie/en/vat/accounting-for-vat/how-to-account-for-value-added-tax/completing-vat3-return.aspx) (Revenue, published 28 July 2026):

| Box | What goes in it |
|---|---|
| T1, VAT on sales | The total VAT due on your supplies of goods and services, on goods you acquire from other EU countries, on imports where you applied VAT postponed accounting, and on services you receive from abroad, as appropriate. |
| T2, VAT on purchases | The total VAT you are entitled to reclaim on costs for goods and services (insofar as they relate to your taxable supplies and qualifying activities), on goods acquired from other EU countries, on imports under postponed accounting, on services received from abroad, and the flat-rate addition. |
| T3, VAT payable | T1 minus T2, when T1 is greater. |
| T4, VAT repayable | T2 minus T1, when T2 is greater. |
| E1 | The total value of goods sent to customers in other EU countries. |
| E2 | The total value of goods received from suppliers in other EU countries. |
| ES1 | The total value of services supplied to customers in other EU countries. |
| ES2 | The total value of services received from suppliers in other EU countries. |
| PA1 | The total Customs value of goods imported under postponed accounting, as per the Customs declarations, plus Customs duty. |

The same page adds two rules:

- A credit note you issue or receive may adjust the T1 and T2 figures.
- If no VAT is payable or repayable for the period, you still send the return, marked zero at T1, T2, T3 and T4. Do not write "nil" on any line.

## When the return is due

From [When VAT becomes payable](https://www.revenue.ie/en/vat/accounting-for-vat/how-to-account-for-value-added-tax/when-vat-becomes-payable.aspx) (Revenue, published 28 July 2026):

- File and pay by the 19th day of the month after the end of each taxable period.
- If you file on ROS (Revenue Online Service), the time limit for filing the return is extended to the 23rd day of the month.
- A taxable period normally lasts two months, starting on 1 January, March, May, July, September and November.
- The Collector-General may authorise four-monthly returns if your annual VAT liability is between €3,001 and €14,400, and six-monthly returns if it is between €1 and €3,000.
- Revenue may authorise monthly returns on request; they are generally for businesses that are always owed a repayment.
- Once a year you also complete a Return of Trading Details (RTD): your total purchases and sales for the year, by VAT rate. The form appears in your ROS inbox.
- Late or missing returns and payments can bring interest and penalties.

## Invoice basis or moneys received basis

From [Who may opt for the moneys received basis?](https://www.revenue.ie/en/vat/accounting-for-vat/accounting-for-vat-on-moneys-received/who-can-opt-moneys-received-basis.aspx) (Revenue, published 10 October 2025):

- On the normal basis, VAT is due when you make the supply or issue the invoice. On the moneys received basis, it is due when you are paid.
- If you are registered for VAT, you may apply for the moneys received basis if your turnover does not exceed, or is not likely to exceed, €2,000,000 in any continuous 12 months, or if at least 90% of your supplies go to customers who are not registered for VAT or cannot claim a full VAT deduction. Revenue says the second condition applies to retailers, public houses, restaurants and similar businesses selling mostly to private individuals.
- It cannot be used for transactions with a connected person, construction services supplied by a sub-contractor to a principal contractor, certain long leases created before 1 July 2008, goods and services acquired from other EU countries, or imports.
- The normal invoicing requirements still apply.

## Services you buy from abroad

Revenue's page on [self-accounting for VAT on received services](https://www.revenue.ie/en/vat/vat-on-services/exceptions-general-place-supply-rules-services/received-services/index.aspx) (published 7 July 2026) gives this example: a business (ABC Ltd in Revenue's example) charged €100,000 for consultancy by a German business accounts for Irish VAT of €23,000 (23%) in T1 of its Irish VAT return, and may also be able to reclaim it in T2 of the same return.

## What to decide with your accountant

- Whether your business uses the invoice basis or the moneys received basis.
- Which of your EU sales and purchases are goods and which are services, for the E and ES boxes.
- Whether you must self-account for VAT on services bought from abroad.
- How a credit note affects the return for the period.

## What the NumWise draft contains

- T1: VAT on sales on your business's basis, set in Settings: on the invoice date (invoice basis) or when your customer pays (moneys received basis), less the VAT on credit notes issued in the period, plus the VAT you account for on purchases from abroad under the reverse charge.
- T2: reclaimable VAT on costs, by the date of the supplier invoice, for the business share of each cost, reverse charge included.
- T3 and T4: the difference between T1 and T2, after each is rounded to whole euro.
- E1, ES1, E2 and ES2: goods and services sold to businesses in other EU countries and bought from suppliers there, from the VAT treatment you choose on each invoice and expense.
- Not included: VAT on goods imported from outside the EU, box PA1 and the VIES return.

The figures are drafts for you and your accountant to check, and the exports say so on their first line. NumWise does not file the return: you file it on ROS.

To see the draft built from your own invoices and costs, [start a 7-day free trial](/register). No card needed.

Related: [VAT rates in Ireland for 2026](/blog/irish-vat-rates-2026-guide) and [the VAT registration threshold](/blog/vat-registration-threshold-ireland).

## Sources

All checked on 27 September 2026.

- Revenue, [How do you complete a VAT 3 return?](https://www.revenue.ie/en/vat/accounting-for-vat/how-to-account-for-value-added-tax/completing-vat3-return.aspx), published 28 July 2026.
- Revenue, [When VAT becomes payable](https://www.revenue.ie/en/vat/accounting-for-vat/how-to-account-for-value-added-tax/when-vat-becomes-payable.aspx), published 28 July 2026.
- Revenue, [Who may opt for the moneys received basis?](https://www.revenue.ie/en/vat/accounting-for-vat/accounting-for-vat-on-moneys-received/who-can-opt-moneys-received-basis.aspx), published 10 October 2025.
- Revenue, [Self-accounting for VAT on received services](https://www.revenue.ie/en/vat/vat-on-services/exceptions-general-place-supply-rules-services/received-services/index.aspx), published 7 July 2026.
`,
  },
  {
    slug: 'vat-registration-threshold-ireland',
    title: 'VAT Registration Threshold in Ireland: When You Must Register',
    seoTitle: 'VAT Registration Threshold in Ireland',
    excerpt: "Register for VAT once annual turnover passes a threshold: €42,500 for services, €85,000 for goods. Revenue's list, how turnover counts and registering early.",
    category: 'VAT Guides',
    imageAlt: 'A glass measuring jug on a workbench, filled with euro coins and notes almost to its line',
    author: 'NumWise editorial team',
    publishedDate: '2026-09-27T12:00:00Z',
    updatedDate: '2026-09-29T12:00:00Z',
    readTime: 4,
    keywords: ['VAT registration threshold Ireland', 'VAT threshold Ireland', 'when to register for VAT', 'sole trader VAT registration'],
    content: `# VAT Registration Threshold in Ireland: When You Must Register

You must register for VAT when your annual turnover exceeds a VAT threshold. Revenue's principal thresholds include €42,500 for a business supplying services only and €85,000 for a business supplying goods. Below a threshold you may still choose to register, for example to reclaim VAT on start-up costs.

Everything below follows Revenue's own pages, which are linked in each section and listed at the end. They were checked on 27 September 2026.

## Revenue's thresholds

From [What are the VAT thresholds?](https://www.revenue.ie/en/vat/vat-registration/who-should-register-for-vat/vat-thresholds.aspx) (Revenue, published 6 May 2026):

| Annual turnover over | Applies to |
|---|---|
| €42,500 | Persons supplying services only. |
| €42,500 | Persons supplying goods at the reduced or standard rate that they manufactured or produced from zero-rated materials. |
| €85,000 | Persons supplying both goods and services, where 90% or more of turnover comes from goods (other than those above). |
| €85,000 | Persons supplying goods. |
| €10,000 | Persons making mail-order or intra-Community distance sales of goods, and cross-border telecommunications, broadcasting and electronic services, into Ireland. The threshold counts such sales to customers in all EU Member States and has conditions set out on Revenue's page. |
| €41,000 | Persons making acquisitions from other EU Member States. |

A business not established in Ireland must register, whatever its turnover, if it supplies taxable goods or services to taxable customers in Ireland, unless it uses the VAT SME Scheme.

## How turnover is counted

Revenue counts the total value, without VAT, in a calendar year of:

1. supplies of taxable goods and services,
2. supplies of immovable goods,
3. certain financial transactions,
4. insurance and reinsurance services.

Left out: occasional disposals of business assets such as buildings, vehicles or machines; transfers of goods to a non-EU country without a supply; and exempt cross-border supplies of new means of transport.

Revenue's example on the same page: a trader sells goods worth €70,000 in 2025 and, once, a delivery van for €20,000. The van is an incidental sale of a business asset, so it is disregarded: Revenue gives the trader's annual turnover for the purposes of the VAT SME Scheme as €70,000.

## Registering early, or below a threshold

From [Who should register for VAT?](https://www.revenue.ie/en/vat/vat-registration/who-should-register-for-vat/index.aspx) (Revenue, published 8 December 2025):

- If you have set up a business but have not yet supplied taxable goods or services, you may reclaim VAT on your start-up costs. To do so, you must register for VAT.
- Traders whose turnover is below the thresholds, farmers and sea fishers are not generally obliged to register, but they may elect to.
- A person carrying out only exempt or non-taxable activities may not register for VAT, but may have to register in certain situations, such as acquiring goods from other EU Member States or receiving services from abroad.

## What to decide with your accountant

- Which threshold fits your mix of goods and services.
- Whether registering before you reach a threshold suits your business.
- Which rate applies to what you sell once you are registered: see [VAT rates in Ireland for 2026](/blog/irish-vat-rates-2026-guide).

## In NumWise

- Say once in Settings whether your business is registered for VAT. While it is not, new invoice lines carry no VAT.
- Once registered, each invoice line takes the rate you choose, and the VAT report and the VAT3 draft are built from your records. See [how to complete a VAT3 return](/blog/how-to-file-vat3-return-ireland).

[Start a 7-day free trial](/register) to set your VAT status and send your first invoice. No card needed.

## Sources

All checked on 27 September 2026.

- Revenue, [What are the VAT thresholds?](https://www.revenue.ie/en/vat/vat-registration/who-should-register-for-vat/vat-thresholds.aspx), published 6 May 2026.
- Revenue, [Who should register for VAT?](https://www.revenue.ie/en/vat/vat-registration/who-should-register-for-vat/index.aspx), published 8 December 2025.`,
  },
  {
    slug: 'proforma-invoice-ireland',
    title: 'Pro Forma Invoice in Ireland: What It Is and When VAT Is Due',
    seoTitle: 'Pro Forma Invoice in Ireland: VAT and Deposits',
    excerpt: "What a pro forma invoice is, how it differs from a quote and a VAT invoice, and Revenue's rule for deposits: VAT falls in the period you are paid.",
    category: 'VAT Guides',
    imageAlt: 'Wrapped kitchen cabinet doors ready to leave a joinery workshop, with a clipboard and a phone on the bench',
    author: 'NumWise editorial team',
    publishedDate: '2026-09-28T12:00:00Z',
    updatedDate: '2026-09-29T12:00:00Z',
    readTime: 4,
    keywords: ['proforma invoice', 'pro forma invoice Ireland', 'what is a proforma invoice', 'pro forma invoice meaning', 'deposit invoice VAT Ireland'],
    content: `# Pro Forma Invoice in Ireland: What It Is and When VAT Is Due

A pro forma invoice is a document you send before you supply goods or services. It sets out what the customer will pay, so they can approve the order or pay in advance. Revenue's VAT pages do not define it, but they set the rules that follow: what a VAT invoice must show, and how VAT is treated when a customer pays before the work is done.

Everything below about VAT follows Revenue's own pages, linked in each section and listed at the end. They were checked on 28 September 2026 and apply to businesses registered for VAT.

## Pro forma, quote or VAT invoice

| Document | What it is for | When it goes out |
|---|---|---|
| Quote | An offer: the price of a job, for the customer to accept or decline. | Before the customer agrees to the work. |
| Pro forma invoice | A request to pay in advance, laid out like an invoice. | After the customer agrees, before you supply. |
| VAT invoice | The document Revenue defines: it records a taxable supply and lets a VAT-registered customer reclaim the VAT. | Within 15 days of the end of the month you supply. For a payment received in advance, see below. |

Our suggestion, not a Revenue rule: mark a pro forma clearly as a pro forma, so your customer does not mistake it for the VAT invoice.

## What a VAT invoice must show

From [What information is required on a VAT invoice?](https://www.revenue.ie/en/vat/vat-records-invoices-credit-notes/invoices/information-required-vat-invoice.aspx) (Revenue, published 16 March 2026), a VAT invoice shows, among other details:

- the date of issue and a unique sequential number,
- your full name, address and VAT registration number,
- the customer's full name and address,
- the quantity and nature of the goods, or the extent and nature of the services,
- the VAT-exclusive unit price, any discounts or price reductions, and the payment received net of VAT,
- the breakdown by VAT rate and the total VAT payable,
- the date the goods or services were supplied, and the date of a payment received in advance where it differs from the date of issue.

From [What is a VAT invoice?](https://www.revenue.ie/en/vat/vat-records-invoices-credit-notes/invoices/what-is-vat-invoice.aspx) (Revenue, published 16 March 2026): a VAT invoice must issue within 15 days of the end of the month in which the goods or services are supplied, and it lets your VAT-registered customers reclaim the VAT charged to them.

## When the customer pays before the work

These rules apply when a customer pays your pro forma before you supply the goods or finish the work. From [Payments in advance and deposits](https://www.revenue.ie/en/vat/charging-vat/payments-in-advance-and-deposits/index.aspx) (Revenue, published 5 November 2025): when you receive a deposit or pre-payment before you have completed the supply, the supply is deemed to take place when you receive the payment. VAT is chargeable on that payment, and you pay it to Revenue in your VAT return.

From [Invoice requirements of payments if received in advance](https://www.revenue.ie/en/vat/vat-records-invoices-credit-notes/vat-invoice-requirements-advance-payments-received/index.aspx) (Revenue, published 18 June 2026):

- Payments may arrive in full or in part before the supply is complete. You must issue a VAT invoice for each payment.
- Issue it no later than the 15th day of the month after the month in which you received the payment.
- This does not apply to intra-Community supplies of goods.

So the VAT on a deposit falls in the period in which the money arrives, even if you finish the job in a later period.

## An example

The figures are for illustration. A customer orders goods at €4,000 before VAT, at the standard rate of 23%: €4,920 in total. You send a pro forma asking for a 30% deposit.

| Step | Date | Amount received | Of which VAT |
|---|---|---:|---:|
| Deposit paid | 20 October | €1,476.00 | €276.00 |
| VAT invoice for the deposit | by 15 November | | |
| Balance paid on delivery | 10 December | €3,444.00 | €644.00 |
| Total | | €4,920.00 | €920.00 |

The VAT in a payment that includes VAT at 23% is the payment × 23 ÷ 123: €1,476 × 23 ÷ 123 = €276.

## What to decide with your accountant

- Whether to ask for deposits, and how much, on the jobs you quote.
- How your invoices for deposits and balances should read, and which VAT period each payment falls in.
- Which VAT rate applies to what you sell: see [VAT rates in Ireland for 2026](/blog/irish-vat-rates-2026-guide).

## In NumWise

- NumWise has no separate pro forma document. Before the customer agrees, a quote does the job: lines, VAT, a valid-until date and a link where the customer accepts or declines. The terms on the quote can set out a deposit, for example 30% on acceptance.
- When a deposit arrives, issue an invoice for that payment, for example one line "Deposit for quote Q-2026-0012", and record the payment against it.
- When the work is done, convert the accepted quote into a draft invoice and edit its lines so it bills the balance.
- To price the job first, use the [margin calculator](/tools/margin-calculator), which works out a price from your cost and adds Irish VAT.

[Start a 7-day free trial](/register) to send your first quote. No card needed.

## Sources

All checked on 28 September 2026.

- Revenue, [What information is required on a VAT invoice?](https://www.revenue.ie/en/vat/vat-records-invoices-credit-notes/invoices/information-required-vat-invoice.aspx), published 16 March 2026.
- Revenue, [What is a VAT invoice?](https://www.revenue.ie/en/vat/vat-records-invoices-credit-notes/invoices/what-is-vat-invoice.aspx), published 16 March 2026.
- Revenue, [Payments in advance and deposits](https://www.revenue.ie/en/vat/charging-vat/payments-in-advance-and-deposits/index.aspx), published 5 November 2025.
- Revenue, [Invoice requirements of payments if received in advance](https://www.revenue.ie/en/vat/vat-records-invoices-credit-notes/vat-invoice-requirements-advance-payments-received/index.aspx), published 18 June 2026.`,
  },
  {
    slug: 'irish-vat-rates-2026-guide',
    title: 'VAT Rates in Ireland for 2026, and Where to Check Yours',
    seoTitle: 'VAT Rates in Ireland for 2026',
    excerpt: "Irish VAT rates from 1 January 2026 (23%, 13.5%, 9%, 4.8% and the farmers' 4.5% flat rate), the changes since 2020 and where to check the rate you charge.",
    category: 'VAT Guides',
    imageAlt: "Three groups of items on a table: coffee and a scone, electrical cable and pipe fittings, soda bread and children's shoes",
    author: 'NumWise editorial team',
    publishedDate: '2026-02-07T12:00:00Z',
    updatedDate: '2026-09-29T12:00:00Z',
    readTime: 4,
    keywords: ['VAT rates Ireland', 'Irish VAT rates 2026', 'standard VAT rate Ireland', 'reduced VAT rate'],
    content: `# VAT Rates in Ireland for 2026, and Where to Check Yours

From 1 January 2026, Revenue lists a standard VAT rate of 23%, a reduced rate of 13.5%, a second reduced rate of 9% and a livestock rate of 4.8%, with a flat-rate compensation percentage of 4.5% for farmers. Which rate applies depends on what you sell, and Revenue's VAT rates database can be searched or browsed A to Z.

Everything below follows Revenue's own pages, which are linked in each section and listed at the end. They were checked on 27 September 2026.

## The rates since 2020

From [Current VAT rates](https://www.revenue.ie/en/vat/vat-rates/search-vat-rates/current-vat-rates.aspx) (Revenue, published 1 January 2026):

| From | Standard | Reduced | Second reduced | Livestock | Farmers' flat rate |
|---|---|---|---|---|---|
| 1 January 2026 | 23% | 13.5% | 9% | 4.8% | 4.5% |
| 1 January 2025 | 23% | 13.5% | 9% | 4.8% | 5.1% |
| 1 January 2024 | 23% | 13.5% | 9% | 4.8% | 4.8% |
| 1 January 2023 | 23% | 13.5% | 9% | 4.8% | 5% |
| 1 January 2022 | 23% | 13.5% | 9% | 4.8% | 5.5% |
| 1 March 2021 | 23% | 13.5% | 9% | 4.8% | 5.6% |
| 1 January 2021 | 21% | 13.5% | 9% | 4.8% | 5.6% |
| 1 September 2020 | 21% | 13.5% | 9% | 4.8% | 5.4% |
| 1 January 2020 | 23% | 13.5% | 9% | 4.8% | 5.4% |

The standard rate was 21% from 1 September 2020 until 1 March 2021, and has been 23% since.

## Finding the rate for what you sell

- Search or browse Revenue's [VAT rates database](https://www.revenue.ie/en/vat/vat-rates/search-vat-rates/vat-rates-database.aspx) (A to Z) for each good or service you sell.
- Check it on the day you need it: rates change.
- If one job combines goods and services, or you are unsure, ask your accountant which rate applies.

## Services you buy from abroad

Revenue's page on [self-accounting for VAT on received services](https://www.revenue.ie/en/vat/vat-on-services/exceptions-general-place-supply-rules-services/received-services/index.aspx) (published 7 July 2026) gives this example: a business (ABC Ltd in Revenue's example) charged €100,000 for consultancy by a German business accounts for Irish VAT of €23,000 (23%) in T1 of its Irish VAT return, and may also be able to reclaim it in T2 of the same return.

## What to decide with your accountant

- Which rate applies to each thing you sell, especially when one job combines goods and services.
- Whether a sale is at the zero rate or exempt from VAT.
- Whether you must self-account for VAT on services you buy from abroad.

## How NumWise records the rate

- Each invoice line has its own VAT rate: 23%, 13.5%, 9%, 4.8% or 0%. NumWise does not choose the rate for you.
- If your business is not VAT registered, invoice lines carry no VAT.
- The VAT report and the VAT3 draft add up the rates you chose. They are drafts for you and your accountant to check, and NumWise files nothing with Revenue.
- VAT on services bought from abroad is self-accounted in the VAT3 draft when you choose a reverse charge as the cost's VAT treatment.

[Start a 7-day free trial](/register) to put the right rate on your own invoice lines. No card needed.

Related: [how to complete a VAT3 return](/blog/how-to-file-vat3-return-ireland) and [the VAT registration threshold](/blog/vat-registration-threshold-ireland).

## Sources

All checked on 27 September 2026.

- Revenue, [Current VAT rates](https://www.revenue.ie/en/vat/vat-rates/search-vat-rates/current-vat-rates.aspx), published 1 January 2026.
- Revenue, [VAT rates database](https://www.revenue.ie/en/vat/vat-rates/search-vat-rates/vat-rates-database.aspx), published 8 September 2026.
- Revenue, [Self-accounting for VAT on received services](https://www.revenue.ie/en/vat/vat-on-services/exceptions-general-place-supply-rules-services/received-services/index.aspx), published 7 July 2026.`,
  },
  {
    slug: 'sole-trader-expenses-ireland',
    title: 'Sole Trader Expenses in Ireland: What You Can Claim',
    seoTitle: 'Sole Trader Expenses in Ireland: What to Claim',
    excerpt: 'The expenses Revenue lets a business deduct, the ones it cannot, how to treat costs you also use privately, and the records to keep for six years.',
    category: 'Tax Guides',
    imageAlt: 'The passenger seat of a work van: a box of receipts, a hi-vis vest and a flask, split from a lunch box by a strip of tape',
    author: 'NumWise editorial team',
    publishedDate: '2026-02-07T12:00:00Z',
    updatedDate: '2026-09-29T12:00:00Z',
    readTime: 5,
    keywords: ['sole trader expenses Ireland', 'allowable expenses Ireland', 'business expenses Revenue', 'keeping records'],
    content: `# Sole Trader Expenses in Ireland: What You Can Claim

When you work out the profit of your business, Revenue lets you deduct expenses directly related to running it, such as goods for resale, rent and bills for your business premises and accountancy fees. You cannot deduct anything not fully related to the business, such as your own pay or business entertainment. For a cost you also use privately, you claim the business part only.

This guide follows Revenue's own pages, linked in each section and listed at the end, all checked on 27 September 2026.

## Expenses you can claim

From [Claiming a deduction for expenses](https://www.revenue.ie/en/starting-a-business/claiming-a-deduction-for-expenses/index.aspx) (Revenue, published 21 October 2025), expenses directly related to running your business include:

- the purchase of goods for resale,
- employees' pay,
- rent and bills for your business premises,
- running costs for vehicles or machines you use in your business,
- lease payments for vehicles or machines you use in your business,
- accountancy fees,
- interest on money you borrowed to finance your business.

You may also claim expenses you had before the business started trading, such as the cost of preparing business plans. If you are registered for VAT, the amount you claim should not include the VAT. Expenses are claimed through ROS on a Form 11 or Form CT1.

## Expenses you cannot claim

You cannot claim for anything not fully related to running your business, such as:

- clothing, except protective clothing,
- your own pay,
- business entertainment expenses,
- your own food or travel, except as described in Revenue's manuals on food and accommodation expenses and on travel expenses.

Capital expenditure, money spent on buying or maintaining land, property or equipment for your business, cannot be deducted when you calculate your profit. You may be able to claim capital allowances on some of it.

## Costs you also use privately

If you spend money on something for both business and private use, such as phone bills, motor expenses and rent, you can claim a deduction for part of it. Work out how much of it was for business purposes and claim that amount only.

## Records to keep

From [Keeping records](https://www.revenue.ie/en/starting-a-business/starting-a-business/keeping-records.aspx) (Revenue, published 20 October 2025):

- Keep anything used to calculate your Income Tax, Corporation Tax or Capital Gains Tax. Revenue calls these linking documents; they can include receipts for expenses and purchases, sales invoices, nominal ledgers and accounting books.
- Keep the originals for six years.
- Your accountant may keep the records for you, but you remain responsible for them.
- You can store digital copies of receipts in Receipts Tracker on ROS.

## What to decide with your accountant

- Which of your costs you can deduct, and which you cannot.
- The business share of anything you also use privately, such as a phone, a car or part of your home, and how you work it out.
- Which purchases are equipment or other capital items rather than day-to-day costs.
- Whether the VAT on a cost can be reclaimed.

## How NumWise records a cost

- Photograph a receipt or upload its PDF. The supplier, date, total and VAT are read for you to check, and the file stays with the cost.
- Choose a category for each cost. What is deductible is for you and your accountant to decide.
- Enter the business share as a percentage. Only that share counts as a business cost and towards reclaimable VAT; the rest is shown as private.
- Mark whether the VAT on the cost is reclaimable. NumWise does not decide this for you.
- Import a bank statement file and match each line to a cost or an invoice.
- Export the costs by category and the profit and loss report to Excel for your accountant.

[Start a 7-day free trial](/register) and photograph your next receipt. No card needed.

Related: [sole trader tax in Ireland for 2026](/blog/sole-trader-tax-estimate-ireland).

## Sources

All checked on 27 September 2026.

- Revenue, [Claiming a deduction for expenses](https://www.revenue.ie/en/starting-a-business/claiming-a-deduction-for-expenses/index.aspx), published 21 October 2025.
- Revenue, [Keeping records](https://www.revenue.ie/en/starting-a-business/starting-a-business/keeping-records.aspx), published 20 October 2025.`,
  },
  {
    slug: 'sole-trader-tax-estimate-ireland',
    title: 'Sole Trader Tax in Ireland for 2026: Income Tax, USC and PRSI',
    seoTitle: 'Sole Trader Tax in Ireland: 2026 Rates',
    excerpt: 'The 2026 income tax bands and credits, USC rates and PRSI Class S rate from Revenue and gov.ie, and the 31 October pay and file date, with every source.',
    category: 'Tax Guides',
    imageAlt: 'Euro notes going into a brown envelope set aside on a kitchen table',
    author: 'NumWise editorial team',
    publishedDate: '2026-02-07T12:00:00Z',
    updatedDate: '2026-09-29T12:00:00Z',
    readTime: 5,
    keywords: ['sole trader tax Ireland', 'income tax bands 2026', 'USC rates 2026', 'PRSI Class S'],
    content: `# Sole Trader Tax in Ireland for 2026: Income Tax, USC and PRSI

For 2026, Revenue lists income tax at 20% on the first €44,000 for a single person without qualifying children and 40% on the balance, a single person tax credit of €2,000 and an earned income tax credit of up to €2,000. The standard USC rates are 0.5%, 2%, 3% and 8% across its bands. Self-employed PRSI (Class S) is 4.2% until 30 September 2026 and 4.35% from 1 October 2026. Your accountant works out which bands and credits apply to you.

This guide follows the official pages, linked in each section and listed at the end, all checked on 27 September 2026.

## Income tax rates and bands for 2026

From [Tax rates, bands and reliefs](https://www.revenue.ie/en/personal-tax-credits-reliefs-and-exemptions/tax-relief-charts/index.aspx) (Revenue, published 1 January 2026):

| Your circumstances | 2026 |
|---|---|
| Single, widowed or surviving civil partner, without qualifying children | €44,000 at 20%, balance at 40% |
| Single, widowed or surviving civil partner, qualifying for the Single Person Child Carer Credit | €48,000 at 20%, balance at 40% |
| Married or in a civil partnership, one spouse or civil partner with income | €53,000 at 20%, balance at 40% |
| Married or in a civil partnership, both with income | €53,000 at 20% (increased by up to €35,000), balance at 40% |

## Tax credits for 2026

From the same page:

| Credit | 2026 |
|---|---|
| Single Person | €2,000 |
| Married Person or Civil Partner | €4,000 |
| Earned Income Tax Credit (maximum) | €2,000 |

Revenue's page lists further credits and reliefs.

## USC for 2026

From [Standard rates and thresholds of USC](https://www.revenue.ie/en/jobs-and-pensions/usc/standard-rates-thresholds.aspx) (Revenue, published 1 January 2026):

| Income | Rate |
|---|---|
| First €12,012 | 0.5% |
| Next €16,688 | 2% |
| Next €41,344 | 3% |
| Balance | 8% |

Added up, the 2% band ends at €28,700 and the 3% band at €70,044 (our arithmetic from Revenue's table).

## PRSI for the self-employed (Class S)

From [PRSI Class S Rates](https://www.gov.ie/en/department-of-social-protection/publications/prsi-class-s-rates/) (Department of Social Protection, last updated 20 January 2026):

- Class S covers self-employed people, including certain company directors and certain people with income from investments and rents.
- The rate is 4.2% of all income until 30 September 2026 and 4.35% from 1 October 2026.
- If you return PRSI through Revenue's self-assessment system, the rate on your 2026 income is a blended 4.2375%, or a minimum payment of €650.
- The minimum annual contribution for Class S is €650.

## Paying and filing

From [Pay and file system](https://www.revenue.ie/en/self-assessment-and-self-employment/guide-to-self-assessment/pay-file-system.aspx) (Revenue, published 12 March 2026):

- By 31 October 2026, pay your preliminary tax for 2026, file your 2025 self-assessment tax return and pay any Income Tax balance for 2025.
- If you pay and file through ROS, the 31 October deadline is extended to 18 November.
- A return filed less than two months late brings a surcharge of 5% of the tax due, up to €12,695; more than two months late, 10%, up to €63,485.

## What NumWise shows

- The profit and loss report: income and costs for any period, from the invoices and costs you recorded.
- For a VAT-registered business, income and costs without VAT, and only the business share of each cost.
- An Excel export of each report, so your accountant does not have to retype the figures.

NumWise does not calculate income tax, USC or PRSI.

[Start a 7-day free trial](/register) to see your own profit and loss. No card needed.

Related: [sole trader expenses in Ireland](/blog/sole-trader-expenses-ireland).

## Sources

All checked on 27 September 2026.

- Revenue, [Tax rates, bands and reliefs](https://www.revenue.ie/en/personal-tax-credits-reliefs-and-exemptions/tax-relief-charts/index.aspx), published 1 January 2026.
- Revenue, [Standard rates and thresholds of USC](https://www.revenue.ie/en/jobs-and-pensions/usc/standard-rates-thresholds.aspx), published 1 January 2026.
- Department of Social Protection, [PRSI Class S Rates](https://www.gov.ie/en/department-of-social-protection/publications/prsi-class-s-rates/), last updated 20 January 2026.
- Revenue, [Pay and file system](https://www.revenue.ie/en/self-assessment-and-self-employment/guide-to-self-assessment/pay-file-system.aspx), published 12 March 2026.`,
  },
]

/** Every post, including those held for review (the prerender writes a noindex page for each). */
/** A guide's photo at one of its three widths. */
export const guideImage = (slug: string, width: 640 | 960 | 1536) => `/images/guides-20260928/${slug}-${width}.webp`

export const allBlogPosts = posts

/** Posts listed on /blog, linked as further reading and included in the sitemap. */
export const blogPosts = posts.filter((post) => !post.heldForReview)
